import React, { createContext, useContext, useReducer, useEffect, useState, useCallback } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { getRandomProfileColor } from '../utils/colorUtils';
import { playersService } from '../services/supabaseService';
import { supabase } from '../lib/supabase';
import { transformPlayerFromDB } from '../utils/dataTransform';

const PlayerContext = createContext();

const playerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PLAYERS':
      return {
        ...state,
        players: action.payload
      };
    case 'ADD_PLAYER':
      const { name, initialBuyIn } = action.payload;
      const newPlayer = {
        id: action.payload.id || Date.now().toString(),
        name: name,
        profileColor: action.payload.profileColor || getRandomProfileColor(),
        totalBuyIn: initialBuyIn ? initialBuyIn.amount : 0,
        totalCashOut: 0,
        buyInHistory: initialBuyIn ? [{
          id: (Date.now() + 1).toString(),
          amount: initialBuyIn.amount,
          timestamp: new Date().toISOString(),
          note: initialBuyIn.note || ''
        }] : [],
        cashOutHistory: [],
        createdAt: action.payload.createdAt || new Date().toISOString()
      };
      return {
        ...state,
        players: [...state.players, newPlayer]
      };
    case 'EDIT_PLAYER':
      return {
        ...state,
        players: state.players.map(player => 
          player.id === action.payload.playerId 
            ? { ...player, name: action.payload.newName }
            : player
        )
      };
    case 'ADD_BUY_IN':
      return {
        ...state,
        players: state.players.map(player => {
          if (player.id === action.payload.playerId) {
            const newBuyIn = {
              id: Date.now().toString(),
              amount: action.payload.amount,
              timestamp: new Date().toISOString(),
              note: action.payload.note || ''
            };
            return {
              ...player,
              totalBuyIn: player.totalBuyIn + action.payload.amount,
              buyInHistory: [...player.buyInHistory, newBuyIn]
            };
          }
          return player;
        })
      };
    case 'ADD_CASH_OUT':
      return {
        ...state,
        players: state.players.map(player => {
          if (player.id === action.payload.playerId) {
            const newCashOut = {
              id: Date.now().toString(),
              amount: action.payload.amount,
              timestamp: new Date().toISOString(),
              note: action.payload.note || ''
            };
            return {
              ...player,
              totalCashOut: (player.totalCashOut || 0) + action.payload.amount,
              cashOutHistory: [...(player.cashOutHistory || []), newCashOut]
            };
          }
          return player;
        })
      };
    case 'DELETE_PLAYER':
      return {
        ...state,
        players: state.players.filter(player => player.id !== action.payload.playerId)
      };
    case 'CLEAR_ALL_PLAYERS':
      return {
        ...state,
        players: [],
        searchTerm: ''
      };
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.payload
      };
    case 'DELETE_BUY_IN':
      return {
        ...state,
        players: state.players.map(player => {
          if (player.id === action.payload.playerId) {
            const buyInToDelete = player.buyInHistory.find(buyIn => buyIn.id === action.payload.buyInId);
            if (buyInToDelete) {
              return {
                ...player,
                totalBuyIn: player.totalBuyIn - buyInToDelete.amount,
                buyInHistory: player.buyInHistory.filter(buyIn => buyIn.id !== action.payload.buyInId)
              };
            }
          }
          return player;
        })
      };
    case 'DELETE_CASH_OUT':
      return {
        ...state,
        players: state.players.map(player => {
          if (player.id === action.payload.playerId) {
            if (player.cashOutHistory && player.cashOutHistory.length > 0) {
              const cashOutToDelete = player.cashOutHistory.find(cashOut => cashOut.id === action.payload.cashOutId);
              if (cashOutToDelete) {
                return {
                  ...player,
                  totalCashOut: (player.totalCashOut || 0) - cashOutToDelete.amount,
                  cashOutHistory: player.cashOutHistory.filter(cashOut => cashOut.id !== action.payload.cashOutId)
                };
              }
            }
          }
          return player;
        })
      };
    case 'MIGRATE_PLAYERS':
      return {
        ...state,
        players: action.payload.map(player => ({
          ...player,
          profileColor: player.profileColor || getRandomProfileColor(),
          totalCashOut: player.totalCashOut || 0,
          cashOutHistory: player.cashOutHistory || []
        }))
      };
    case 'UPDATE_PLAYER':
      return {
        ...state,
        players: state.players.map(player =>
          player.id === action.payload.playerId
            ? { ...player, ...action.payload.updates }
            : player
        )
      };
    default:
      return state;
  }
};

const initialState = {
  players: [],
  searchTerm: ''
};

import { isSupabaseConfigured } from '../lib/supabase';

export const PlayerProvider = ({ children, sessionId = null }) => {
  const [state, dispatch] = useReducer(playerReducer, initialState);
  const storageKey = sessionId ? `pokerPlayers_${sessionId}` : 'pokerPlayers';
  const [storedPlayers, setStoredPlayers] = useLocalStorage(storageKey, []);
  const [useSupabase, setUseSupabase] = useState(isSupabaseConfigured() && sessionId);

  // Normalize player data - recalculate totals from history if needed
  const normalizePlayer = (player) => {
    // Recalculate totalCashOut from cashOutHistory if history exists
    if (player.cashOutHistory && player.cashOutHistory.length > 0) {
      const calculatedTotalCashOut = player.cashOutHistory.reduce((sum, cashOut) => {
        return sum + (parseFloat(cashOut.amount) || 0);
      }, 0);
      
      // Update totalCashOut if it doesn't match the calculated value
      if (Math.abs((player.totalCashOut || 0) - calculatedTotalCashOut) > 0.01) {
        player.totalCashOut = calculatedTotalCashOut;
        
        // If using Supabase, update the database with the corrected value (async, don't wait)
        if (useSupabase && sessionId) {
          playersService.update(player.id, {
            totalCashOut: calculatedTotalCashOut
          }).catch(error => {
            console.error(`Error updating totalCashOut for player ${player.id}:`, error);
          });
        }
      }
    }
    
    return player;
  };

  // Load players function - extracted for reuse
  const loadPlayers = useCallback(async () => {
    if (useSupabase && sessionId) {
      try {
        let players = await playersService.getBySession(sessionId);
        // Normalize all players to ensure totals match history
        players = players.map(normalizePlayer);
        
        const needsMigration = players.some(player => 
          !player.profileColor || 
          player.totalCashOut === undefined || 
          !player.cashOutHistory
        );
        
        if (needsMigration) {
          dispatch({ type: 'MIGRATE_PLAYERS', payload: players });
        } else {
          dispatch({ type: 'SET_PLAYERS', payload: players });
        }
      } catch (error) {
        console.error('Error loading players from Supabase:', error);
        setUseSupabase(false);
        let players = storedPlayers.map(normalizePlayer);
        const needsMigration = players.some(player => 
          !player.profileColor || 
          player.totalCashOut === undefined || 
          !player.cashOutHistory
        );
        
        if (needsMigration) {
          dispatch({ type: 'MIGRATE_PLAYERS', payload: players });
        } else {
          dispatch({ type: 'SET_PLAYERS', payload: players });
        }
      }
    } else {
      let players = storedPlayers.map(normalizePlayer);
      const needsMigration = players.some(player => 
        !player.profileColor || 
        player.totalCashOut === undefined || 
        !player.cashOutHistory
      );
      
      if (needsMigration) {
        dispatch({ type: 'MIGRATE_PLAYERS', payload: players });
      } else {
        dispatch({ type: 'SET_PLAYERS', payload: players });
      }
    }
  }, [sessionId, useSupabase, storedPlayers]);

  // Load players on mount
  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  // Auto-refresh players every 10 seconds (only when using Supabase)
  useEffect(() => {
    if (!useSupabase || !sessionId) return;

    const refreshInterval = setInterval(() => {
      loadPlayers();
    }, 10000); // 10 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [useSupabase, sessionId, loadPlayers]);

  // Set up real-time subscription for player changes
  useEffect(() => {
    if (!useSupabase || !sessionId) return;

    const channel = supabase
      .channel(`players:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'players',
          filter: `session_id=eq.${sessionId}`
        },
        async (payload) => {
          // Reload players from database to get the latest state
          try {
            let players = await playersService.getBySession(sessionId);
            // Normalize all players to ensure totals match history
            players = players.map(normalizePlayer);
            const needsMigration = players.some(player => 
              !player.profileColor || 
              player.totalCashOut === undefined || 
              !player.cashOutHistory
            );
            
            if (needsMigration) {
              dispatch({ type: 'MIGRATE_PLAYERS', payload: players });
            } else {
              dispatch({ type: 'SET_PLAYERS', payload: players });
            }
          } catch (error) {
            console.error('Error reloading players after real-time update:', error);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Real-time subscription error');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [useSupabase, sessionId]);

  // Sync to localStorage if not using Supabase
  useEffect(() => {
    if (!useSupabase && state.players.length >= 0) {
      setStoredPlayers(state.players);
    }
  }, [state.players, useSupabase, setStoredPlayers]);

  const addPlayer = async (name, initialBuyIn = null) => {
    const playerData = {
      name,
      profileColor: getRandomProfileColor(),
      totalBuyIn: initialBuyIn ? initialBuyIn.amount : 0,
      totalCashOut: 0,
      buyInHistory: initialBuyIn ? [{
        id: (Date.now() + 1).toString(),
        amount: initialBuyIn.amount,
        timestamp: new Date().toISOString(),
        note: initialBuyIn.note || ''
      }] : [],
      cashOutHistory: [],
    };

    if (useSupabase && sessionId) {
      try {
        const newPlayer = await playersService.create(sessionId, playerData);
        dispatch({ type: 'ADD_PLAYER', payload: { ...newPlayer, initialBuyIn } });
        return newPlayer.id;
      } catch (error) {
        console.error('Error creating player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ADD_PLAYER', payload: { name, initialBuyIn } });
    }
  };

  const editPlayer = async (playerId, newName) => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Player name cannot be empty');
    }
    
    const isDuplicate = state.players.some(player => 
      player.id !== playerId && 
      player.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isDuplicate) {
      throw new Error('A player with this name already exists');
    }
    
    if (useSupabase) {
      try {
        await playersService.update(playerId, { name: trimmedName });
        dispatch({ type: 'EDIT_PLAYER', payload: { playerId, newName: trimmedName } });
      } catch (error) {
        console.error('Error updating player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'EDIT_PLAYER', payload: { playerId, newName: trimmedName } });
    }
  };

  const addBuyIn = async (playerId, amount, note = '') => {
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;

    const updatedPlayer = {
      ...player,
      totalBuyIn: player.totalBuyIn + amount,
      buyInHistory: [...player.buyInHistory, {
        id: Date.now().toString(),
        amount,
        timestamp: new Date().toISOString(),
        note
      }]
    };

    if (useSupabase) {
      try {
        await playersService.update(playerId, {
          totalBuyIn: updatedPlayer.totalBuyIn,
          buyInHistory: updatedPlayer.buyInHistory,
        });
        dispatch({ type: 'ADD_BUY_IN', payload: { playerId, amount, note } });
      } catch (error) {
        console.error('Error adding buy-in:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ADD_BUY_IN', payload: { playerId, amount, note } });
    }
  };

  const addCashOut = async (playerId, amount, note = '') => {
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;

    const updatedPlayer = {
      ...player,
      totalCashOut: (player.totalCashOut || 0) + amount,
      cashOutHistory: [...(player.cashOutHistory || []), {
        id: Date.now().toString(),
        amount,
        timestamp: new Date().toISOString(),
        note
      }]
    };

    if (useSupabase) {
      try {
        await playersService.update(playerId, {
          totalCashOut: updatedPlayer.totalCashOut,
          cashOutHistory: updatedPlayer.cashOutHistory,
        });
        dispatch({ type: 'ADD_CASH_OUT', payload: { playerId, amount, note } });
      } catch (error) {
        console.error('Error adding cash-out:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ADD_CASH_OUT', payload: { playerId, amount, note } });
    }
  };

  const deleteBuyIn = async (playerId, buyInId) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;

    const buyInToDelete = player.buyInHistory.find(buyIn => buyIn.id === buyInId);
    if (!buyInToDelete) return;

    const updatedPlayer = {
      ...player,
      totalBuyIn: player.totalBuyIn - buyInToDelete.amount,
      buyInHistory: player.buyInHistory.filter(buyIn => buyIn.id !== buyInId)
    };

    if (useSupabase) {
      try {
        await playersService.update(playerId, {
          totalBuyIn: updatedPlayer.totalBuyIn,
          buyInHistory: updatedPlayer.buyInHistory,
        });
        dispatch({ type: 'DELETE_BUY_IN', payload: { playerId, buyInId } });
      } catch (error) {
        console.error('Error deleting buy-in:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'DELETE_BUY_IN', payload: { playerId, buyInId } });
    }
  };

  const deleteCashOut = async (playerId, cashOutId) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || !player.cashOutHistory) return;

    const cashOutToDelete = player.cashOutHistory.find(cashOut => cashOut.id === cashOutId);
    if (!cashOutToDelete) return;

    const updatedPlayer = {
      ...player,
      totalCashOut: (player.totalCashOut || 0) - cashOutToDelete.amount,
      cashOutHistory: player.cashOutHistory.filter(cashOut => cashOut.id !== cashOutId)
    };

    if (useSupabase) {
      try {
        await playersService.update(playerId, {
          totalCashOut: updatedPlayer.totalCashOut,
          cashOutHistory: updatedPlayer.cashOutHistory,
        });
        dispatch({ type: 'DELETE_CASH_OUT', payload: { playerId, cashOutId } });
      } catch (error) {
        console.error('Error deleting cash-out:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'DELETE_CASH_OUT', payload: { playerId, cashOutId } });
    }
  };

  const deletePlayer = async (playerId) => {
    if (useSupabase) {
      try {
        await playersService.delete(playerId);
        dispatch({ type: 'DELETE_PLAYER', payload: { playerId } });
      } catch (error) {
        console.error('Error deleting player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'DELETE_PLAYER', payload: { playerId } });
    }
  };

  const clearAllPlayers = async () => {
    if (useSupabase && sessionId) {
      try {
        await playersService.clearAll(sessionId);
        dispatch({ type: 'CLEAR_ALL_PLAYERS' });
      } catch (error) {
        console.error('Error clearing players:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'CLEAR_ALL_PLAYERS' });
      setStoredPlayers([]);
    }
  };

  const setSearchTerm = (term) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: term });
  };

  const getFilteredPlayers = () => {
    if (!state.searchTerm) return state.players;
    return state.players.filter(player => 
      player.name.toLowerCase().includes(state.searchTerm.toLowerCase())
    );
  };

  const getPlayerById = (id) => {
    return state.players.find(player => player.id === id);
  };

  const getTotalBuyIns = () => {
    return state.players.reduce((total, player) => total + player.totalBuyIn, 0);
  };

  const getTotalCashOuts = () => {
    return state.players.reduce((total, player) => total + (player.totalCashOut || 0), 0);
  };

  const getNetProfit = () => {
    return getTotalCashOuts() - getTotalBuyIns();
  };

  const value = {
    ...state,
    addPlayer,
    editPlayer,
    addBuyIn,
    addCashOut,
    deleteBuyIn,
    deleteCashOut,
    deletePlayer,
    clearAllPlayers,
    setSearchTerm,
    getFilteredPlayers,
    getPlayerById,
    getTotalBuyIns,
    getTotalCashOuts,
    getNetProfit
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export default PlayerContext;
